import { useState } from 'react';
import { Calendar, TrendingUp, FileText, BookOpen, Trash2, Pencil, Loader2 } from 'lucide-react';
import { LibraryItem, LibrarySection } from './types';

interface LibraryDataGridProps {
  items: LibraryItem[];
  loading: boolean;
  viewMode: 'grid' | 'list';
  onItemSelect: (item: LibraryItem) => void;
  onItemDelete?: (item: LibraryItem) => void;
  onItemEdit?: (item: LibraryItem) => void;
}

const getSectionIcon = (type: LibrarySection) => {
  const icons: Record<LibrarySection, typeof FileText> = {
    precedents: BookOpen,
  };
  return icons[type] || FileText;
};

export default function LibraryDataGrid({ items, loading, viewMode, onItemSelect, onItemDelete, onItemEdit }: LibraryDataGridProps) {
  const [hoveredEditBtn, setHoveredEditBtn] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div style={{ color: "var(--text-secondary)" }}>Loading...</div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="empty-state">
            <p className="mb-2" style={{ color: "var(--text-secondary)" }}>No items found</p>
            <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>Create your first item to get started</p>
        </div>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item) => {
          const Icon = getSectionIcon(item.type);
          const processing = item.analyzed === false;
          const editBtnId = `edit-grid-${item.id}`;
          const deleteBtnId = `delete-grid-${item.id}`;

          return (
            <div
              key={item.id}
              onClick={() => onItemSelect(item)}
              className="card-base card-interactive cursor-pointer flex flex-col p-5 group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-5 h-5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                  <h3 className="font-medium text-sm truncate" style={{ color: "var(--text-primary)" }}>{item.title}</h3>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  {onItemEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onItemEdit(item); }}
                      className="p-1 rounded"
                      style={{ backgroundColor: hoveredEditBtn === editBtnId ? "var(--secondary)" : "transparent" }}
                      onMouseEnter={() => setHoveredEditBtn(editBtnId)}
                      onMouseLeave={() => setHoveredEditBtn(null)}
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
                    </button>
                  )}
                  {onItemDelete && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onItemDelete(item); }}
                      className="p-1 hover:bg-red-100 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--error)" }} />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1">
                {processing ? (
                  <div className="flex items-center gap-2 text-xs py-2" style={{ color: "var(--muted-foreground)" }}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '2s' }} />
                    <span>Analyzing document...</span>
                  </div>
                ) : item.description ? (
                  <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.description}</p>
                ) : null}
              </div>

              <div className="flex items-center justify-between text-xs mt-4 pt-3" style={{ color: "var(--text-secondary)", borderTop: "1px solid var(--border)" }}>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>{item.usageCount} uses</span>
                </div>
                {item.lastUsed && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{new Date(item.lastUsed).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // List view
  return (
    <div className="overflow-auto">
      <table className="w-full">
        <thead className="sticky top-0" style={{ backgroundColor: "var(--secondary)", borderBottom: "1px solid var(--border)" }}>
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "var(--text-primary)" }}>Title</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "var(--text-primary)" }}>Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "var(--text-primary)" }}>Usage</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "var(--text-primary)" }}>Last Used</th>
            {(onItemEdit || onItemDelete) && (
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider w-24" style={{ color: "var(--text-primary)" }}></th>
            )}
          </tr>
        </thead>
        <tbody style={{ backgroundColor: "var(--surface-alt)" }}>
          {items.map((item) => {
            const Icon = getSectionIcon(item.type);
            const processing = item.analyzed === false;
            const editBtnId = `edit-list-${item.id}`;
            const deleteBtnId = `delete-list-${item.id}`;

            return (
              <tr
                key={item.id}
                onClick={() => onItemSelect(item)}
                className="group cursor-pointer transition-colors"
                style={{
                  backgroundColor: hoveredRow === item.id ? "var(--secondary)" : "transparent",
                  borderBottom: "1px solid var(--border)",
                }}
                onMouseEnter={() => setHoveredRow(item.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
                    <div>
                      <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{item.title}</div>
                      {item.description && (
                        <div className="text-xs truncate max-w-md" style={{ color: "var(--text-secondary)" }}>{item.description}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {processing ? (
                    <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--muted-foreground)" }}>
                      <Loader2 className="w-3 h-3 animate-spin" style={{ animationDuration: '2s' }} />
                      <span>Analyzing...</span>
                    </div>
                  ) : (
                    <span className="text-xs" style={{ color: "var(--success)" }}>Ready</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                    <TrendingUp className="w-3 h-3" />
                    <span>{item.usageCount}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: "var(--text-secondary)" }}>
                  {item.lastUsed ? new Date(item.lastUsed).toLocaleDateString() : '-'}
                </td>
                {(onItemEdit || onItemDelete) && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      {onItemEdit && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onItemEdit(item); }}
                          className="p-1 rounded"
                          style={{ backgroundColor: hoveredEditBtn === editBtnId ? "var(--secondary)" : "transparent" }}
                          onMouseEnter={() => setHoveredEditBtn(editBtnId)}
                          onMouseLeave={() => setHoveredEditBtn(null)}
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
                        </button>
                      )}
                      {onItemDelete && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onItemDelete(item); }}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--error)" }} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
